// src/components/Customers.js

import React, { useEffect, useMemo, useState } from "react";
import config from "../config";
import "../styles/Customers.css";

export default function Customers() {

  const [customers, setCustomers] = useState([]);

  const [summary, setSummary] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("ALL");

  // =========================================
  // FETCH CUSTOMER INSIGHTS
  // =========================================

  useEffect(() => {

    fetch(`${config.baseUrl}/api/admin/customers/insights`)
      .then((response) => {

        if (!response.ok) {
          throw new Error("Failed to fetch customer insights");
        }

        return response.json();
      })
      .then((data) => {

        setCustomers(data.customers || []);

        setSummary(data.summary);

        setLoading(false);
      })
      .catch((err) => {

        console.error(err);

        setError("Error loading customer insights");

        setLoading(false);
      });

  }, []);

  // =========================================
  // FILTERED CUSTOMERS
  // =========================================

  const filteredCustomers = useMemo(() => {

    if (activeTab === "ALL") {
      return customers;
    }

    if (activeTab === "REPEAT_BUYERS") {

      return customers.filter(
        (c) => (c.totalOrders || 0) >= 5
      );
    }

    if (activeTab === "NEW_USERS") {

      const sevenDaysAgo = new Date();

      sevenDaysAgo.setDate(
        sevenDaysAgo.getDate() - 7
      );

      return customers.filter((c) => {

        if (!c.joinedDate) {
          return false;
        }

        return new Date(c.joinedDate) >= sevenDaysAgo;
      });
    }

    return customers.filter(
      (c) => c.customerType === activeTab
    );

  }, [customers, activeTab]);

  // =========================================
  // HELPERS
  // =========================================

  const formatCurrency = (amount) => {

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {

    if (!date) {
      return "N/A";
    }

    return new Date(date).toLocaleDateString();
  };

  // =========================================
  // LOADING
  // =========================================

  if (loading) {
    return <p className="loading">Loading customer insights...</p>;
  }

  if (error) {
    return <p className="error">{error}</p>;
  }

  // =========================================
  // UI
  // =========================================

  return (

    <div className="customers-container">

      {/* =========================================
          HEADER
      ========================================= */}

      <div className="customers-header">

        <h2>Customer Intelligence</h2>

        <p>
          Track customer behavior, online/offline buyers,
          repeat customers, and conversion opportunities
        </p>

      </div>

      {/* =========================================
          SUMMARY CARDS
      ========================================= */}

      {summary && (

        <div className="customers-summary-grid">

          {/* TOTAL USERS */}

          <div
            className={`summary-card ${
              activeTab === "ALL"
                ? "selected-card"
                : ""
            }`}
            onClick={() => setActiveTab("ALL")}
          >

            <p className="summary-label">
              Total Users
            </p>

            <h3>{summary.totalUsers}</h3>

          </div>

          {/* NEW USERS */}

          <div
            className={`summary-card new-users ${
              activeTab === "NEW_USERS"
                ? "selected-card"
                : ""
            }`}
            onClick={() => setActiveTab("NEW_USERS")}
          >

            <p className="summary-label">
              New to Aurakart
            </p>

            <h3>{summary.newUsersLast7Days}</h3>

            <span className="summary-subtext">
              Joined in last 7 days
            </span>

          </div>

          {/* ONLINE */}

          <div
            className={`summary-card online ${
              activeTab === "ONLINE"
                ? "selected-card"
                : ""
            }`}
            onClick={() => setActiveTab("ONLINE")}
          >

            <p className="summary-label">
              Online Customers
            </p>

            <h3>{summary.onlineCustomers}</h3>

          </div>

          {/* OFFLINE */}

          <div
            className={`summary-card offline ${
              activeTab === "OFFLINE"
                ? "selected-card"
                : ""
            }`}
            onClick={() => setActiveTab("OFFLINE")}
          >

            <p className="summary-label">
              Offline Customers
            </p>

            <h3>{summary.offlineCustomers}</h3>

          </div>

          {/* NEVER ORDERED */}

          <div
            className={`summary-card never-ordered ${
              activeTab === "NEVER_ORDERED"
                ? "selected-card"
                : ""
            }`}
            onClick={() => setActiveTab("NEVER_ORDERED")}
          >

            <p className="summary-label">
              Never Ordered
            </p>

            <h3>{summary.neverOrderedUsers}</h3>

            <span className="summary-subtext">
              Conversion opportunity
            </span>

          </div>

          {/* REPEAT BUYERS */}

          <div
            className={`summary-card repeat ${
              activeTab === "REPEAT_BUYERS"
                ? "selected-card"
                : ""
            }`}
            onClick={() => setActiveTab("REPEAT_BUYERS")}
          >

            <p className="summary-label">
              Repeat Buyers
            </p>

            <h3>{summary.repeatBuyers}</h3>

            <span className="summary-subtext">
              5+ orders placed
            </span>

          </div>

        </div>
      )}

      {/* =========================================
          TABLE
      ========================================= */}

      <div className="customers-table-wrapper">

        <table className="customers-table">

          <thead>

          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Phone</th>
            <th>Email</th>
            <th>Orders</th>
            <th>Total Spent</th>
            <th>Last Order</th>
            <th>Status</th>
          </tr>

          </thead>

          <tbody>

          {filteredCustomers.length === 0 ? (

            <tr>
              <td colSpan="8" className="empty-state">
                No customers found
              </td>
            </tr>

          ) : (

            filteredCustomers.map((cust, index) => (

              <tr key={cust.customerId || index}>

                <td>

                  <div className="customer-name-block">

                    <div className="customer-name">
                      {cust.name || "N/A"}
                    </div>

                    {cust.customerId && (
                      <div className="customer-id">
                        ID #{cust.customerId}
                      </div>
                    )}

                  </div>

                </td>

                <td>

                  <span
                    className={`customer-type-badge ${(
                      cust.customerType || ""
                    ).toLowerCase()}`}
                  >
                    {cust.customerType}
                  </span>

                </td>

                <td>
                  {cust.phone || "N/A"}
                </td>

                <td>
                  {cust.email || "N/A"}
                </td>

                <td>
                  {cust.totalOrders || 0}
                </td>

                <td>
                  {formatCurrency(cust.totalSpent)}
                </td>

                <td>
                  {formatDate(cust.lastOrderDate)}
                </td>

                <td>

                  <span
                    className={
                      cust.active
                        ? "status-active"
                        : "status-inactive"
                    }
                  >
                    {cust.active ? "Active" : "Inactive"}
                  </span>

                </td>

              </tr>

            ))

          )}

          </tbody>

        </table>

      </div>

    </div>
  );
}